import _toConsumableArray from "/usr/src/app/www/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/usr/src/app/www/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useRef, useState } from "react";
import { useControlledState } from "@react-stately/utils";
import { clamp, snapValueToStep } from "@react-aria/utils";
var $dcc38d2f5fc04b76254f325fa36d$var$DEFAULT_MIN_VALUE = 0;
var $dcc38d2f5fc04b76254f325fa36d$var$DEFAULT_MAX_VALUE = 100;
var $dcc38d2f5fc04b76254f325fa36d$var$DEFAULT_STEP_VALUE = 1;
/**
 * Provides state management for a slider component. Stores values for all thumbs,
 * formats values for localization, and provides methods to update the position
 * of any thumbs.
 * @param props
 */

export function useSliderState(props) {
  var _props$defaultValue;

  var isDisabled = props.isDisabled,
      _props$minValue = props.minValue,
      minValue = _props$minValue === void 0 ? $dcc38d2f5fc04b76254f325fa36d$var$DEFAULT_MIN_VALUE : _props$minValue,
      _props$maxValue = props.maxValue,
      maxValue = _props$maxValue === void 0 ? $dcc38d2f5fc04b76254f325fa36d$var$DEFAULT_MAX_VALUE : _props$maxValue,
      formatter = props.numberFormatter,
      _props$step = props.step,
      step = _props$step === void 0 ? $dcc38d2f5fc04b76254f325fa36d$var$DEFAULT_STEP_VALUE : _props$step;

  var _useControlledState = useControlledState(props.value, (_props$defaultValue = props.defaultValue) != null ? _props$defaultValue : [minValue], props.onChange),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      values = _useControlledState2[0],
      setValues = _useControlledState2[1];

  var _useState = useState(new Array(values.length).fill(false)),
      _useState2 = _slicedToArray(_useState, 2),
      isDraggings = _useState2[0],
      setDraggings = _useState2[1];

  var isEditablesRef = useRef(new Array(values.length).fill(true));

  var _useState3 = useState(undefined),
      _useState4 = _slicedToArray(_useState3, 2),
      focusedIndex = _useState4[0],
      setFocusedIndex = _useState4[1];

  var valuesRef = useRef(null);
  valuesRef.current = values;
  var isDraggingsRef = useRef(null);
  isDraggingsRef.current = isDraggings;

  function getValuePercent(value) {
    return (value - minValue) / (maxValue - minValue);
  }

  function getThumbMinValue(index) {
    return index === 0 ? minValue : values[index - 1];
  }

  function getThumbMaxValue(index) {
    return index === values.length - 1 ? maxValue : values[index + 1];
  }

  function isThumbEditable(index) {
    return isEditablesRef.current[index];
  }

  function setThumbEditable(index, editable) {
    isEditablesRef.current[index] = editable;
  }

  function updateValue(index, value) {
    if (isDisabled || !isThumbEditable(index)) {
      return;
    }

    var thisMin = getThumbMinValue(index);
    var thisMax = getThumbMaxValue(index); // Round value to multiple of step, clamp value between min and max

    value = snapValueToStep(value, thisMin, thisMax, step);
    valuesRef.current = $dcc38d2f5fc04b76254f325fa36d$var$replaceIndex(valuesRef.current, index, value);
    setValues(valuesRef.current);
  }

  function updateDragging(index, dragging) {
    if (isDisabled || !isThumbEditable(index)) {
      return;
    }

    var wasDragging = isDraggingsRef.current[index];
    isDraggingsRef.current = $dcc38d2f5fc04b76254f325fa36d$var$replaceIndex(isDraggingsRef.current, index, dragging);
    setDraggings(isDraggingsRef.current); // Call onChangeEnd if no handles are dragging.

    if (props.onChangeEnd && wasDragging && !isDraggingsRef.current.some(Boolean)) {
      props.onChangeEnd(valuesRef.current);
    }
  }

  function getFormattedValue(value) {
    return formatter.format(value);
  }

  function setThumbPercent(index, percent) {
    updateValue(index, getPercentValue(percent));
  }

  function getRoundedValue(value) {
    return Math.round((value - minValue) / step) * step + minValue;
  }

  function getPercentValue(percent) {
    var val = percent * (maxValue - minValue) + minValue;
    return clamp(getRoundedValue(val), minValue, maxValue);
  }

  return {
    values: values,
    getThumbValue: function getThumbValue(index) {
      return values[index];
    },
    setThumbValue: updateValue,
    setThumbPercent: setThumbPercent,
    isThumbDragging: function isThumbDragging(index) {
      return isDraggings[index];
    },
    setThumbDragging: updateDragging,
    focusedThumb: focusedIndex,
    setFocusedThumb: setFocusedIndex,
    getThumbPercent: function getThumbPercent(index) {
      return getValuePercent(values[index]);
    },
    getValuePercent: getValuePercent,
    getThumbValueLabel: function getThumbValueLabel(index) {
      return getFormattedValue(values[index]);
    },
    getFormattedValue: getFormattedValue,
    getThumbMinValue: getThumbMinValue,
    getThumbMaxValue: getThumbMaxValue,
    getPercentValue: getPercentValue,
    isThumbEditable: isThumbEditable,
    setThumbEditable: setThumbEditable,
    step: step
  };
}

function $dcc38d2f5fc04b76254f325fa36d$var$replaceIndex(array, index, value) {
  if (array[index] === value) {
    return array;
  }

  return [].concat(_toConsumableArray(array.slice(0, index)), [value], _toConsumableArray(array.slice(index + 1)));
}